import React, { ReactNode } from "react";

// --- Types ---
export type SortDirection = "asc" | "desc" | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyField?: keyof T;
  onRowClick?: (row: T) => void;
  sortConfig?: SortConfig | null;
  onSort?: (key: string) => void;
}

interface PanelProps {
  title?: string;
  children: ReactNode;
  right?: ReactNode;
}

// --- Components ---

export function Panel({ title, children, right }: PanelProps) {
  const showHeader = Boolean(title) || Boolean(right);
  return (
    <section className="panel">
      {showHeader && (
        <div className="panel-header">
          {title && <h2 className="panel-title">{title}</h2>}
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Table<T extends Record<string, any>>({
  columns,
  rows,
  keyField = "id",
  onRowClick,
  sortConfig,
  onSort,
}: TableProps<T>) {
  const handleHeaderClick = (column: Column<T>) => {
    if (column.sortable && onSort) {
      onSort(String(column.key));
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="table w-full">
        <thead className="text-left bg-slate-50 text-slate-600">
          <tr>
            {columns.map((c) => {
              const isSorted = sortConfig?.key === c.key;
              const isSortable = Boolean(c.sortable && onSort);

              return (
                <th
                  key={String(c.key)}
                  className={`px-3 py-2 font-medium text-left ${
                    isSortable
                      ? "cursor-pointer hover:bg-slate-100 select-none"
                      : ""
                  }`}
                  onClick={() => handleHeaderClick(c)}
                >
                  <div className="th-content">
                    {c.header}

                    {isSorted && (
                      <span className="sort-icon active">
                        {sortConfig?.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                    {!isSorted && isSortable && (
                      <span className="sort-icon inactive">↕</span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr
              key={String(r[keyField as string] || JSON.stringify(r))}
              onClick={() => onRowClick && onRowClick(r)}
              className={
                onRowClick
                  ? "cursor-pointer hover:bg-slate-50 transition-colors"
                  : ""
              }
            >
              {columns.map((c) => (
                <td
                  key={String(c.key)}
                  className="px-3 py-2 align-top text-left"
                >
                  {c.render ? c.render(r) : (r[c.key as keyof T] as ReactNode)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function sortData<T>(
  data: T[],
  sortConfig: SortConfig | null,
  // Optional: rules to extract values for specific complex columns
  customExtractors: Record<string, (item: T) => any> = {}
): T[] {
  if (!data || !sortConfig) return data || [];

  return [...data].sort((a, b) => {
    const { key, direction } = sortConfig;

    // get values (use custom extractor if provided, else default access)
    let aValue = customExtractors[key]
      ? customExtractors[key](a)
      : a[key as keyof T];
    let bValue = customExtractors[key]
      ? customExtractors[key](b)
      : b[key as keyof T];

    // handle null/undefined
    if (aValue === null || aValue === undefined) aValue = "";
    if (bValue === null || bValue === undefined) bValue = "";

    let comparison = 0;

    if (typeof aValue === "string" && typeof bValue === "string") {
      // String comparison (case-insensitive, handles accents)
      comparison = aValue.localeCompare(bValue);
    } else {
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;
    }

    return direction === "asc" ? comparison : -comparison;
  });
}

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="empty">{children}</div>
);

export const Badge = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => <span className={`badge ${className}`}>{children}</span>;

export const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="detail-section">
    <h3 className="detail-section-title">{title}</h3>
    <div>{children}</div>
  </div>
);

export const DetailRow = ({
  label,
  value,
  isBool = false,
  isArray = false,
}: {
  label: string;
  value: any;
  isBool?: boolean;
  isArray?: boolean;
}) => {
  let content;

  if (isArray && Array.isArray(value)) {
    content =
      value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <span key={index} className="detail-value-array-item">
              {typeof item === "object" && item !== null && "name" in item
                ? item.name
                : String(item)}
            </span>
          ))}
        </div>
      ) : (
        <span style={{ color: "#d1d5db" }}>—</span>
      );
  } else if (isBool) {
    content = value ? (
      <span className="bool-yes">SI</span>
    ) : (
      <span className="bool-no">NO</span>
    );
  } else {
    const displayValue =
      value !== null && value !== undefined && value !== ""
        ? String(value)
        : "—";

    content = (
      <span
        style={{ color: displayValue === "—" ? "#d1d5db" : "#374151" }}
        title={displayValue}
      >
        {displayValue}
      </span>
    );
  }

  return (
    <div className="detail-row">
      <span className="detail-label">{label}:</span>
      <div className="detail-value">{content}</div>
    </div>
  );
};
